import { combineEpics, ofType, StateObservable } from 'redux-observable';
import { Observable, of } from 'rxjs';
import { catchError, debounceTime, filter, map, mergeMap, startWith } from 'rxjs/operators';
import { requestActions } from './apiHelpers';
import AppState from '../reducers/app-state';
import { ReduxAction } from '@repo/utils/types';
import { apiObservable } from '@repo/utils/helpers/api';
import { AuraOptions, selectAuraUserOptions } from '../reducers/user/aura';

/* Epic actions */
const reconnectNumberPerDayEpic = requestActions('RECONNECT_NUMBER_PER_DAY', ['fetch', 'update']);
const auraSettingsEpic = requestActions('AURA_SETTINGS', ['fetch', 'update']);

/* Component actions */
export const fetchReconnectNumberPerDay = reconnectNumberPerDayEpic['fetch'];
export const updateReconnectNumberPerDay = reconnectNumberPerDayEpic['update'];

export const fetchAuraSettings = auraSettingsEpic['fetch'];
export const updateAuraSettings = (settings: Partial<AuraOptions>) =>
  auraSettingsEpic['update'](settings);
export const upgradePlanAndEnableAura = () => ({ type: 'UPGRADE_PLAN_AND_ENABLE_AURA' });

/* Selectors */
export const reconnectNumberPerDaySelector = (state: AppState) =>
  state.data.userSettings.reconnectNumberPerDay;

/* Epics */
export const fetchReconnectNumberPerDayEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('FETCH_RECONNECT_NUMBER_PER_DAY'),
    mergeMap(() =>
      apiObservable('GET', '/users/reconnect-settings/').pipe(
        map(reconnectNumberPerDayEpic['fetchSucceeded']),
        startWith(reconnectNumberPerDayEpic['fetchRequested']()),
        catchError((err) => of(reconnectNumberPerDayEpic['fetchFailed'](err))),
      ),
    ),
  );

export const updateReconnectNumberPerDayEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('UPDATE_RECONNECT_NUMBER_PER_DAY'),
    mergeMap(({ payload }) =>
      apiObservable('PATCH', '/users/reconnect-settings/', {
        number_per_day: payload.numberPerDay,
      }).pipe(
        map(reconnectNumberPerDayEpic['updateSucceeded']),
        startWith(reconnectNumberPerDayEpic['updateRequested']()),
        catchError((err) => of(reconnectNumberPerDayEpic['updateFailed'](err))),
      ),
    ),
  );

const fetchAuraSettingsEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('FETCH_AURA_SETTINGS'),
    mergeMap(() =>
      apiObservable('GET', '/enrichment/usage/', undefined, { apiVersion: 'v2' }).pipe(
        map(auraSettingsEpic['fetchSucceeded']),
        startWith(auraSettingsEpic['fetchRequested']()),
        catchError((err) => of(auraSettingsEpic['fetchFailed'](err))),
      ),
    ),
  );

const refetchAuraSettingsOnUpdateEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('UPDATE_AURA_SETTINGS_SUCCEEDED'),
    filter(({ payload }) => payload.enabling),
    map(() => ({ type: 'FETCH_AURA_SETTINGS' })),
  );

// We will syncronously update settings in the store, but debounce the API call
const updateAuraSettingsEpic = (
  action$: Observable<ReduxAction>,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    ofType('UPDATE_AURA_SETTINGS'),
    debounceTime(400),
    mergeMap(() => {
      const auraSettings = selectAuraUserOptions(state$.value);
      const { enabling, ...settingsUpdates } = auraSettings;

      return apiObservable('PATCH', '/enrichment/settings/', settingsUpdates, {
        apiVersion: 'v2',
      }).pipe(
        map((resp) => auraSettingsEpic['updateSucceeded']({ ...resp, enabling })),
        startWith(auraSettingsEpic['updateRequested']()),
        catchError((err) => of(auraSettingsEpic['updateFailed'](err))),
      );
    }),
  );

const upgradePlanAndEnableAuraEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('UPGRADE_PLAN_AND_ENABLE_AURA'),
    mergeMap(() =>
      apiObservable(
        'PATCH',
        '/enrichment/settings/',
        { is_enabled: true, end_trial_immediately: true },
        { apiVersion: 'v2' },
      ).pipe(
        map((resp) => auraSettingsEpic['updateSucceeded']({ ...resp, enabling: true })),
        startWith(auraSettingsEpic['updateRequested']()),
        catchError((err) => of(auraSettingsEpic['updateFailed'](err))),
      ),
    ),
  );

export const userSettingsEpic = combineEpics<ReduxAction, any, AppState>(
  fetchReconnectNumberPerDayEpic,
  updateReconnectNumberPerDayEpic,
  fetchAuraSettingsEpic,
  updateAuraSettingsEpic,
  upgradePlanAndEnableAuraEpic,
  refetchAuraSettingsOnUpdateEpic,
);
